import type React from 'react';
import { Avatar, Tag, Tooltip } from 'antd';
import startCase from 'lodash/startCase';
import dayjs from 'dayjs';
import type { PlanIndexColumnConfig } from '../config/tpoGenConfig';

const STATUS_TAG_CONFIG: Record<string, { color: string }> = {
    draft: { color: 'processing' },
    rejected: { color: 'error' },
    active: { color: 'success' },
    executed: { color: 'default' },
    in_progress: { color: 'warning' },
    approved: { color: 'success' },
};

const EMAIL_SEGMENT_RE = /[._-]/;

const getInitialsFromEmail = (email: string): string => {
    const namePart = email.split('@')[0];
    const segments = namePart.split(EMAIL_SEGMENT_RE);
    return segments
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join('');
};

const renderAvatar = (text: string): React.ReactNode => {
    if (!text) return null;
    const initials = getInitialsFromEmail(text);
    return (
        <div className="flex items-center gap-2">
            <Avatar size={32}>{initials}</Avatar>
            <span>{text}</span>
        </div>
    );
};

const renderStatus = (text: string): React.ReactNode => {
    if (!text) return null;
    const config = STATUS_TAG_CONFIG[text.toLowerCase()] ?? { color: 'default' };
    // The rendered label is start-cased and colour-coded, so the raw status is
    // exposed as state for assertions that must not depend on either.
    return (
        <Tag style={{ borderRadius: 4 }} color={config.color} data-state-status={text.toLowerCase()}>
            {startCase(text)}
        </Tag>
    );
};

const renderDate = (ts: number | string): React.ReactNode => {
    if (!ts) return null;
    const parsed = typeof ts === 'number' ? dayjs.unix(ts) : dayjs(ts);
    return (
        <Tooltip title={parsed.toDate().toLocaleString()} showArrow={false}>
            <span>{parsed.fromNow()}</span>
        </Tooltip>
    );
};

const renderCount = (count: number): React.ReactNode => {
    if (count == null) return null;
    return (
        <span className="inline-flex items-center rounded-lg bg-[#F7F5FF] px-3 py-px text-xs font-medium text-[#44338A]">
            {count}
        </span>
    );
};

const renderText = (value: string | number): React.ReactNode => {
    if (value == null) return null;
    return <span>{value}</span>;
};

const CELL_RENDERERS: Record<PlanIndexColumnConfig['type'], (value: any) => React.ReactNode> = {
    avatar: renderAvatar,
    status: renderStatus,
    date: renderDate,
    count: renderCount,
    text: renderText,
};

export default CELL_RENDERERS;
